<template>
  <div>
    <a-card style="margin: 20px" title="重试管理">
      <a-alert
          message="本页仅展示【服务费失败】的订单。重试仅重新收取失败的服务费，不会重发订单付款；处理中的记录系统不会自动重发，请人工核实后再操作。"
          show-icon
          style="margin-bottom: 16px;"
          type="warning"
      />

      <!-- 筛选区域 -->
      <div class="selectBox">
        <div class="item">
          <span>订单号</span>
          <a-input v-model.trim="paramsData.orderNo" allow-clear placeholder="请输入订单号" style="width: 200px"/>
        </div>
        <div class="item">
          <span>商户协议编号</span>
          <a-input v-model.trim="paramsData.merchantNo" allow-clear placeholder="请输入商户协议编号" style="width: 180px"/>
        </div>
        <div class="item">
          <span>创建时间</span>
          <a-range-picker
              v-model="timeRange"
              :show-time="{format: 'HH:mm:ss'}"
              format="YYYY-MM-DD HH:mm:ss"
              style="width: 360px"
          />
        </div>
        <div class="item">
          <a-button type="primary" @click="handleQuery">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </div>
      </div>

      <!-- 表格部分 -->
      <div class="tableBox">
        <div class="handleBtn">
          <a-button type="primary" @click="handleQuery">刷新</a-button>
        </div>
        <div class="tableBody">
          <a-table
              :columns="columns"
              :data-source="tableData"
              :pagination="false"
              :row-key="record => record.orderNo"
              :scroll="{x: 1200}"
          >
            <template slot="merchantInfo" slot-scope="text, record">
              <div>{{ record.merchantNo }}</div>
              <div style="font-size: 12px; color: #999;">{{ record.merchantName }}</div>
            </template>
            <template slot="payer" slot-scope="text, record">
              <WalletInfo :account-name="record.payerName" :account-no="text" :show-account-name="true"/>
            </template>
            <template slot="totalAmount" slot-scope="text">
              <Balance :amount="text" prefix="￥"/>
            </template>
            <template slot="failReason" slot-scope="text">
              <a-tooltip v-if="text" :title="text">
                <span class="fail-reason">{{ text }}</span>
              </a-tooltip>
              <span v-else>-</span>
            </template>
            <template slot="action" slot-scope="text, record">
              <a-button-group size="small">
                <a-button icon="eye" style="color: #1890ff;" type="link" @click="viewBtn(record)">查看</a-button>
                <a-button
                    v-if="$authority('payAgrRetryExecute')"
                    :loading="retryLoadingNo === record.orderNo"
                    icon="sync" style="color: #ff7a45;" type="link"
                    @click="retryBtn(record)"
                >重试服务费
                </a-button>
              </a-button-group>
            </template>
          </a-table>
        </div>
      </div>

      <!-- 分页部分 -->
      <div class="pagination-wrapper">
        <LcfsPagination :fn="getTableData" :page-no.sync="pagination.pageNo" :page-size.sync="pagination.pageSize" :total="pagination.total"/>
      </div>
    </a-card>
  </div>
</template>

<script>
import {message} from "ant-design-vue";
import LcfsPagination from "@/view/components/LcfsPagination.vue";
import Balance from "@/view/components/Balance.vue";
import WalletInfo from "@/view/components/WalletInfo.vue";

export default {
  name: 'PayAgrRetry',
  components: {LcfsPagination, Balance, WalletInfo},
  data() {
    return {
      pagination: {pageNo: 1, pageSize: 10, total: 0},
      tableData: [],
      timeRange: [],
      paramsData: {
        orderNo: '',
        merchantNo: '',
      },
      retryLoadingNo: null,
      columns: [
        {
          title: "No",
          customRender: (text, record, index) => `${(this.pagination.pageNo - 1) * this.pagination.pageSize + (index + 1)}`,
          align: "center",
          width: 50
        },
        {title: "订单号", dataIndex: "orderNo", align: "center", width: 180},
        {title: "商户协议", dataIndex: "merchantNo", align: "center", width: 160, scopedSlots: {customRender: 'merchantInfo'}},
        {title: "付款方", dataIndex: "payerAccount", align: "center", width: 160, scopedSlots: {customRender: 'payer'}},
        {title: "订单总金额", dataIndex: "totalAmount", align: "center", width: 120, scopedSlots: {customRender: 'totalAmount'}},
        {title: "失败原因", dataIndex: "failReason", align: "center", width: 200, scopedSlots: {customRender: 'failReason'}},
        {title: "支付时间", dataIndex: "payTime", align: "center", width: 150, customRender: (text) => text || '-'},
        {title: "创建时间", dataIndex: "createTime", align: "center", width: 150, customRender: (text) => text || '-'},
        {title: "操作", key: "action", width: 220, align: "center", fixed: 'right', scopedSlots: {customRender: 'action'}},
      ],
    };
  },
  mounted() {
    this.getTableData();
  },
  methods: {
    buildTimeParams() {
      // a-range-picker 绑定的是 moment 对象，优先用 moment.format
      const fmt = (m) => (m && typeof m.format === 'function') ? m.format('YYYY-MM-DD HH:mm:ss') : '';
      if (this.timeRange && this.timeRange.length === 2) {
        return {createTimeStart: fmt(this.timeRange[0]), createTimeEnd: fmt(this.timeRange[1])};
      }
      return {createTimeStart: '', createTimeEnd: ''};
    },

    async getTableData() {
      try {
        // 复用订单列表接口，固定 status=4（服务费失败）
        const res = await this.api.getPayAgrOrderList({
          ...this.paramsData,
          ...this.buildTimeParams(),
          status: 4,
          pageNo: this.pagination.pageNo,
          pageSize: this.pagination.pageSize,
        });
        if (res.code === 200) {
          this.tableData = res.data.records || [];
          this.pagination.total = res.data.total || 0;
        } else {
          message.warning(res.info || "获取数据失败");
        }
      } catch (error) {
        console.error("获取数据失败:", error);
        message.error("获取数据失败");
      }
    },

    handleQuery() {
      this.pagination.pageNo = 1;
      this.getTableData();
    },

    handleReset() {
      this.paramsData = {orderNo: '', merchantNo: ''};
      this.timeRange = [];
      this.pagination.pageNo = 1;
      this.getTableData();
    },

    viewBtn(record) {
      this.$router.push({path: '/pay-agr/order/detail', query: {orderNo: record.orderNo}});
    },

    retryBtn(record) {
      this.$confirm({
        title: '重试服务费',
        content: `确认重新收取订单【${record.orderNo}】中失败的服务费？不会重发订单付款。`,
        onOk: async () => {
          this.retryLoadingNo = record.orderNo;
          try {
            const res = await this.api.retryPayAgrServiceFee({orderNo: record.orderNo});
            if (res.code === 200 && res.data) {
              const status = res.data.status;
              if (status === 3) {
                message.success(`订单 ${record.orderNo} 服务费全部收取成功，订单完成`);
              } else if (status === 4) {
                message.warning(`订单 ${record.orderNo} 仍有服务费失败，可继续重试`);
              } else if (status === 2) {
                message.warning(`订单 ${record.orderNo} 存在处理中的记录，系统不会自动重发，请稍后核实结果`);
              }
              this.getTableData();
            } else {
              message.warning(res.info || '重试失败');
            }
          } catch (error) {
            console.error('重试失败:', error);
            message.error('重试失败');
          } finally {
            this.retryLoadingNo = null;
          }
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.selectBox {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 16px;

  .item {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    margin-right: 20px;

    span {
      margin-right: 8px;
      white-space: nowrap;
    }
  }
}

.tableBox {
  margin-bottom: 20px;

  .handleBtn {
    margin-bottom: 16px;

    .ant-btn {
      margin-right: 8px;
    }
  }
}

.fail-reason {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ff4d4f;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding: 12px 0;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 6px 6px;
}
</style>
